"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.OrcadDsnCfbRecordDecoders = void 0;
const DsnCfbBinarySpec_1 = require("./DsnCfbBinarySpec");
const OrcadDsnCfbPagePropertyFrames_1 = require("./OrcadDsnCfbPagePropertyFrames");
const OrcadDsnCfbTypedObjectFrames_1 = require("./OrcadDsnCfbTypedObjectFrames");
const OrcadDsnCfbDeclarativeSpecs_1 = require("./OrcadDsnCfbDeclarativeSpecs");
const OrcadDsnCfbStreamSpecs_1 = require("./OrcadDsnCfbStreamSpecs");
const OrcadDsnCfbStreamUtils_1 = require("./OrcadDsnCfbStreamUtils");
const OrcadDsnCfbTypes_1 = require("./OrcadDsnCfbTypes");
var OrcadDsnCfbRecordDecoders;
(function (OrcadDsnCfbRecordDecoders) {
    const CFB_STREAM_ENTRY_TYPE = 2;
    const CACHE_STREAM_SPEC_NAME = 'cache';
    const SOURCE_LIBRARY_PATH_PATTERN = /\.(?:OLB|DSN)$/i;
    function shouldIncludeRawRecords(options) {
        return options.includeRawRecords === true;
    }
    function shouldIncludeDiagnostics(options) {
        return options.includeDiagnostics === true || options.includeRawRecords === true;
    }
    function withLibraryStringCatalog(options) {
        return {
            ...options,
            extra: {
                ...options.extra,
                libraryStringCatalog: options.libraryStringCatalog,
            },
        };
    }
    function createPrimitiveCounts() {
        return {
            'comment-text': 0,
            rect: 0,
            ellipse: 0,
            polygon: 0,
            polyline: 0,
            arc: 0,
            line: 0,
            bitmap: 0,
        };
    }
    function createPageObjectCounts() {
        return {
            wire: 0,
            alias: 0,
            'placed-symbol': 0,
            'drawn-inst': 0,
            'drawn-port': 0,
            'display-property': 0,
            'pin-to-wire': 0,
            'port-instance': 0,
            'bus-entry': 0,
            junction: 0,
        };
    }
    function createSymbolObjectCounts() {
        return {
            'view-header': 0,
            'symbol-pin': 0,
            'display-property': 0,
            'part-value': 0,
            'pin-number-map': 0,
            'named-record': 0,
        };
    }
    function hasDecodedPrimitives(counts) {
        return Object.values(counts).some((count) => count > 0);
    }
    OrcadDsnCfbRecordDecoders.hasDecodedPrimitives = hasDecodedPrimitives;
    function hasDecodedPageObjects(counts) {
        return Object.values(counts).some((count) => count > 0);
    }
    OrcadDsnCfbRecordDecoders.hasDecodedPageObjects = hasDecodedPageObjects;
    function hasDecodedSymbolObjects(counts) {
        return Object.values(counts).some((count) => count > 0);
    }
    OrcadDsnCfbRecordDecoders.hasDecodedSymbolObjects = hasDecodedSymbolObjects;
    function decodePrimitiveRecordNode(bytes, offset, options = {}) {
        for (const spec of OrcadDsnCfbDeclarativeSpecs_1.OrcadDsnCfbDeclarativeSpecs.ORCAD_DSN_CFB_PRIMITIVE_RECORD_SPECS) {
            const decoded = DsnCfbBinarySpec_1.DsnCfbBinarySpec.decodeRecordSpec(spec, {
                bytes,
                offset,
                streamPath: options.streamPath ?? '',
                streamName: options.streamName,
            });
            if (decoded) {
                return decoded;
            }
        }
        return undefined;
    }
    function compactPolygonDiagnostic(bytes, offset, options) {
        if (bytes[offset] !== 0x2c ||
            bytes[offset + 1] !== 0x2c ||
            (bytes[offset + 2] !== 0x2e && bytes[offset + 2] !== 0x2a)) {
            return undefined;
        }
        const base = {
            severity: 'warning',
            specName: 'primitive.polygon',
            streamPath: options.streamPath ?? '',
            offset,
        };
        if (offset + 28 > bytes.length) {
            return { ...base, code: 'compact-polygon-truncated-header', message: 'Compact polygon header is truncated' };
        }
        const pointCount = bytes[offset + 26] | (bytes[offset + 27] << 8);
        if (bytes[offset + 2] === 0x2a &&
            (pointCount !== 4 ||
                [10, 14, 18, 22].some((field) => OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, offset + field) !== 0))) {
            return {
                ...base,
                code: 'compact-polygon-layout-unverified',
                message: 'Four-point polygon count or style fields lack a corroborated layout',
                details: { pointCount },
            };
        }
        if (pointCount < 2 || pointCount > 4096) {
            return {
                ...base,
                code: 'compact-polygon-impossible-point-count',
                message: `Compact polygon has impossible point count ${pointCount}`,
                details: { pointCount },
            };
        }
        const terminatorOffset = offset + 28 + pointCount * 4;
        if (terminatorOffset + 8 > bytes.length) {
            return {
                ...base,
                code: 'compact-polygon-truncated-points',
                message: `Compact polygon point payload is truncated for ${pointCount} points`,
                details: { pointCount },
            };
        }
        const terminator = [0xff, 0xe4, 0x5c, 0x39, 0x00, 0x00, 0x00, 0x00];
        if (!terminator.every((byte, index) => bytes[terminatorOffset + index] === byte)) {
            return {
                ...base,
                code: 'compact-polygon-invalid-terminator',
                message: 'Compact polygon has an invalid record terminator',
                details: { pointCount },
            };
        }
        return {
            ...base,
            code: 'compact-polygon-invalid-coordinates',
            message: 'Compact polygon failed validation',
            details: { pointCount },
        };
    }
    function decodeOrCadDsnCfbPrimitiveRecords(bytes, options = {}) {
        const maxRecords = options.maxRecords ?? Number.MAX_SAFE_INTEGER;
        const records = [];
        const decodedRecords = [];
        const diagnostics = [];
        const counts = createPrimitiveCounts();
        let truncated = false;
        let decodedByteCount = 0;
        for (let offset = 0; offset < bytes.length; offset++) {
            const decoded = decodePrimitiveRecordNode(bytes, offset, options);
            if (!decoded) {
                if (shouldIncludeDiagnostics(options)) {
                    const diagnostic = compactPolygonDiagnostic(bytes, offset, options);
                    if (diagnostic) {
                        diagnostics.push(diagnostic);
                    }
                }
                continue;
            }
            const record = decoded.value;
            counts[record.kind]++;
            decodedByteCount += record.byteLength;
            if (records.length < maxRecords) {
                records.push(record);
                if (shouldIncludeRawRecords(options)) {
                    decodedRecords.push(decoded);
                }
            }
            else {
                truncated = true;
            }
            if (shouldIncludeDiagnostics(options)) {
                diagnostics.push(...decoded.diagnostics);
            }
            offset += record.byteLength - 1;
        }
        return {
            records,
            counts,
            truncated,
            decodedByteCount,
            ...(shouldIncludeRawRecords(options) ? { decodedRecords } : {}),
            ...(shouldIncludeDiagnostics(options) ? { diagnostics } : {}),
        };
    }
    OrcadDsnCfbRecordDecoders.decodeOrCadDsnCfbPrimitiveRecords = decodeOrCadDsnCfbPrimitiveRecords;
    function junctionPageObjectsFromWire(wire) {
        return (wire.junctions ?? []).map((junction) => ({
            kind: 'junction',
            offset: wire.offset,
            byteLength: wire.byteLength,
            payloadByteLength: wire.payloadByteLength,
            wireDbId: wire.dbId,
            endpoint: junction.endpoint,
            locX: junction.locX,
            locY: junction.locY,
        }));
    }
    function decodePageRecordSpecNode(spec, record, options = {}) {
        return DsnCfbBinarySpec_1.DsnCfbBinarySpec.decodeRecordSpec(spec, {
            bytes: record.payload,
            offset: 0,
            streamPath: options.streamPath ?? '',
            streamName: options.streamName,
            payloadOffset: record.offset,
            payloadByteLength: record.payloadByteLength,
            byteLength: record.byteLength,
            extra: options.extra,
        });
    }
    function derivedPageRecordNode(kind, value, record, options = {}) {
        return {
            kind,
            family: 'derived',
            value,
            span: {
                streamPath: options.streamPath ?? '',
                offset: value.offset,
                byteLength: value.byteLength,
                payloadOffset: record.offset,
                payloadByteLength: record.payloadByteLength,
            },
            specName: `page.derived-${kind}`,
            confidence: 'derived',
            diagnostics: [],
        };
    }
    function scanDrawnInstPageObjectNodes(record, options = {}) {
        const records = [];
        for (let offset = 0; offset + 24 <= record.payload.length; offset++) {
            for (const spec of OrcadDsnCfbDeclarativeSpecs_1.OrcadDsnCfbDeclarativeSpecs.ORCAD_DSN_CFB_PAGE_SCAN_RECORD_SPECS) {
                const decoded = DsnCfbBinarySpec_1.DsnCfbBinarySpec.decodeRecordSpec(spec, {
                    bytes: record.payload,
                    offset,
                    streamPath: options.streamPath ?? '',
                    streamName: options.streamName,
                    payloadOffset: record.offset + offset,
                    extra: options.extra,
                });
                if (!decoded) {
                    continue;
                }
                records.push(decoded);
                offset += 23;
                break;
            }
        }
        return records;
    }
    function scanEmbeddedWirePageObjectNode(record, options = {}) {
        const innerOffset = 43;
        if (record.payload.length < innerOffset + 52 ||
            !OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.hasBytes(record.payload, 0, [0x27, 0x00, 0x00, 0x00, 0x27, 0x00, 0x00, 0x00])) {
            return undefined;
        }
        return DsnCfbBinarySpec_1.DsnCfbBinarySpec.decodeRecordSpec(OrcadDsnCfbDeclarativeSpecs_1.OrcadDsnCfbDeclarativeSpecs.ORCAD_DSN_CFB_PAGE_RECORD_SPECS[0], {
            bytes: record.payload,
            offset: innerOffset,
            streamPath: options.streamPath ?? '',
            streamName: options.streamName,
            payloadOffset: record.offset + innerOffset,
            payloadByteLength: record.payloadByteLength - innerOffset,
            byteLength: record.byteLength - innerOffset,
        });
    }
    function decodePageObjectRecordNodes(record, options = {}) {
        const wire = decodePageRecordSpecNode(OrcadDsnCfbDeclarativeSpecs_1.OrcadDsnCfbDeclarativeSpecs.ORCAD_DSN_CFB_PAGE_RECORD_SPECS[0], record, options);
        if (wire) {
            const junctions = junctionPageObjectsFromWire(wire.value).map((junction) => derivedPageRecordNode('junction', junction, record, options));
            const placedSymbol = decodePageRecordSpecNode(OrcadDsnCfbDeclarativeSpecs_1.OrcadDsnCfbDeclarativeSpecs.ORCAD_DSN_CFB_PAGE_RECORD_SPECS[3], record, options);
            return [wire, ...junctions, ...(placedSymbol ? [placedSymbol] : [])];
        }
        const decoded = OrcadDsnCfbDeclarativeSpecs_1.OrcadDsnCfbDeclarativeSpecs.ORCAD_DSN_CFB_PAGE_RECORD_SPECS.slice(1)
            .map((spec) => decodePageRecordSpecNode(spec, record, options))
            .filter((pageObject) => Boolean(pageObject));
        if (decoded.some((pageObject) => pageObject.kind === 'placed-symbol')) {
            return decoded;
        }
        const embeddedWire = scanEmbeddedWirePageObjectNode(record, options);
        return [...decoded, ...(embeddedWire ? [embeddedWire] : []), ...scanDrawnInstPageObjectNodes(record, options)];
    }
    function attachDisplayPropertyToPlacedSymbol(placedSymbol, displayProperty) {
        placedSymbol.displayProperties ??= [];
        placedSymbol.displayProperties.push(displayProperty);
        if (displayProperty.displayedValue === undefined || displayProperty.displayedValue.length === 0) {
            return;
        }
        placedSymbol.instanceProperties ??= [];
        placedSymbol.instanceProperties.push({
            name: displayProperty.propertyName,
            value: displayProperty.displayedValue,
            propertyCode: displayProperty.propertyCode,
            source: 'display-property',
        });
    }
    function attachPortInstanceToPlacedSymbol(placedSymbol, portInstance) {
        placedSymbol.portInstances ??= [];
        placedSymbol.portInstances.push(portInstance);
    }
    function attachDisplayPropertyToDrawnInst(drawnInst, displayProperty) {
        drawnInst.displayProperties ??= [];
        drawnInst.displayProperties.push(displayProperty);
    }
    function attachDrawnPortToDrawnInst(drawnInst, drawnPort) {
        drawnInst.portInstances ??= [];
        drawnInst.portInstances.push(drawnPort);
    }
    function attachPinToWireToPlacedSymbol(placedSymbol, pinToWire) {
        placedSymbol.connectedPins ??= [];
        placedSymbol.connectedPins.push(pinToWire);
    }
    function attachOwnerToPlacedSymbolChild(record, placedSymbol) {
        if (!placedSymbol) {
            return record;
        }
        return {
            ...record,
            ownerDbId: placedSymbol.dbId,
            ownerSymbolName: placedSymbol.symbolName,
            ownerRole: placedSymbol.role,
        };
    }
    function attachOwnerToDrawnInstPort(record, drawnInst) {
        if (!drawnInst) {
            return record;
        }
        return {
            ...record,
            ownerDbId: drawnInst.dbId,
            ownerRole: 'drawn-inst',
        };
    }
    function decodeOrCadDsnCfbPageObjects(bytes, options = {}) {
        const resolvedOptions = withLibraryStringCatalog(options);
        const diagnostics = [];
        const pinCandidates = (0, OrcadDsnCfbTypedObjectFrames_1.readTypedObjectFrames)(bytes, [0x10, 0x11], 1, diagnostics, resolvedOptions.streamPath);
        const ownerCandidates = (0, OrcadDsnCfbTypedObjectFrames_1.readTypedObjectFrames)(bytes, [0x0d, 0x25], undefined, diagnostics, resolvedOptions.streamPath);
        const drawnCandidates = (0, OrcadDsnCfbTypedObjectFrames_1.readTypedObjectFrames)(bytes, [0x0c], undefined, diagnostics, resolvedOptions.streamPath);
        const displayCandidates = (0, OrcadDsnCfbTypedObjectFrames_1.readTypedObjectFrames)(bytes, [0x27], 1, diagnostics, resolvedOptions.streamPath);
        const conflictedDataOffsets = new Set([
            ...(0, OrcadDsnCfbTypedObjectFrames_1.findCrossKindFrameConflicts)([
                { frames: pinCandidates, role: OrcadDsnCfbTypedObjectFrames_1.TypedObjectFrameRole.Pin },
                { frames: ownerCandidates, role: OrcadDsnCfbTypedObjectFrames_1.TypedObjectFrameRole.Owner },
                { frames: drawnCandidates, role: OrcadDsnCfbTypedObjectFrames_1.TypedObjectFrameRole.Owner },
                { frames: displayCandidates, role: OrcadDsnCfbTypedObjectFrames_1.TypedObjectFrameRole.Display },
            ], diagnostics, options.streamPath),
            ...diagnostics
                .filter((diagnostic) => diagnostic.code === 'typed-frame-data-offset-collision')
                .map((diagnostic) => diagnostic.offset),
        ]);
        const typedPins = new Map([...pinCandidates].filter(([offset]) => !conflictedDataOffsets.has(offset)));
        const typedOwners = new Map([...ownerCandidates].filter(([offset]) => !conflictedDataOffsets.has(offset)));
        const drawnOwners = [...drawnCandidates.values()].filter((frame) => !conflictedDataOffsets.has(frame.dataOffset));
        const displayDataFrames = new Map([...displayCandidates].filter(([offset]) => !conflictedDataOffsets.has(offset)));
        const displayFrames = new Map([...displayDataFrames.values()].map((frame) => [frame.offset, frame]));
        const placementDisplayCounts = new Map();
        const maxRecords = resolvedOptions.maxRecords ?? Number.MAX_SAFE_INTEGER;
        const records = [];
        const decodedRecords = [];
        const counts = createPageObjectCounts();
        let truncated = false;
        let decodedByteCount = 0;
        let currentPlacedSymbol;
        let currentDrawnInst;
        let currentDrawnFrame;
        let pendingPropertyPairsPayload;
        let pendingAliasOwner;
        OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.forEachPagePayload(bytes, (payload) => {
            if (conflictedDataOffsets.has(payload.offset)) {
                diagnostics.push({
                    severity: 'warning',
                    code: 'typed-frame-owner-context-withheld',
                    streamPath: options.streamPath ?? '',
                    offset: payload.offset,
                    message: `CFB page ownership: conflicting payload interrupts placement=${currentPlacedSymbol?.dbId}, drawing=${currentDrawnInst?.dbId}, wire=${pendingAliasOwner}; following children cannot inherit these owners`,
                });
                currentPlacedSymbol = undefined;
                currentDrawnInst = undefined;
                currentDrawnFrame = undefined;
                pendingAliasOwner = undefined;
                pendingPropertyPairsPayload = undefined;
                return;
            }
            const reportPropertyFrame = (reason) => {
                diagnostics.push({
                    severity: 'warning',
                    code: 'page-property-frame-withheld',
                    streamPath: options.streamPath ?? '',
                    offset: payload.offset,
                    message: `CFB page property [page-property-frame-withheld] ${reason}`,
                });
            };
            const aliasOwner = pendingAliasOwner;
            pendingAliasOwner = undefined;
            const pinFrame = typedPins.get(payload.offset);
            const ownerFrame = currentPlacedSymbol === undefined ? currentDrawnFrame : typedOwners.get(currentPlacedSymbol.offset);
            let ownsPin = pinFrame !== undefined &&
                ownerFrame !== undefined &&
                pinFrame.offset >= ownerFrame.dataOffset &&
                pinFrame.endOffset <= ownerFrame.endOffset &&
                pinFrame.dataOffset + 16 <= pinFrame.endOffset;
            if (ownsPin && pinFrame) {
                const count = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, pinFrame.dataOffset + 14);
                let cursor = pinFrame.dataOffset + 16;
                for (let index = 0; index < count; index++) {
                    const child = displayFrames.get(cursor);
                    if (!child || child.endOffset > pinFrame.endOffset) {
                        ownsPin = false;
                        break;
                    }
                    cursor = child.endOffset;
                }
                ownsPin = ownsPin && cursor === pinFrame.endOffset;
            }
            if (pinFrame && !ownsPin) {
                diagnostics.push({
                    severity: 'warning',
                    code: 'page-pin-frame-withheld',
                    offset: payload.offset,
                    streamPath: options.streamPath ?? '',
                    message: `CFB page pin [page-pin-frame-withheld] offset=${payload.offset}: incomplete counted children or missing framed placement owner`,
                });
            }
            const decodedNodes = decodePageObjectRecordNodes(payload, {
                ...resolvedOptions,
                extra: {
                    ...resolvedOptions.extra,
                    ownedConnectivityFrame: ownsPin,
                    placementDisplayCounts,
                    displayFrameEnd: displayDataFrames.get(payload.offset)?.endOffset,
                },
            });
            if (aliasOwner !== undefined &&
                !decodedNodes.some((node) => node.value.kind === 'alias' && node.value.offset === payload.offset)) {
                diagnostics.push({
                    severity: 'warning',
                    code: 'wire-alias-child-unresolved',
                    streamPath: options.streamPath ?? '',
                    offset: payload.offset,
                    message: `CFB alias [wire-alias-child-unresolved] wire=${aliasOwner}: counted child is not a decoded alias`,
                });
            }
            if (decodedNodes.length === 0) {
                pendingPropertyPairsPayload = payload.payload;
                return;
            }
            decodedByteCount += payload.byteLength;
            let closesCurrentPlacedSymbol = false;
            let closesCurrentDrawnInst = false;
            for (const decodedNode of decodedNodes) {
                const decodedRecord = decodedNode.value;
                const record = decodedRecord.kind === 'pin-to-wire' || decodedRecord.kind === 'port-instance' ?
                    attachOwnerToPlacedSymbolChild(decodedRecord, currentPlacedSymbol)
                    : decodedRecord.kind === 'drawn-port' ? attachOwnerToDrawnInstPort(decodedRecord, currentDrawnInst)
                        : decodedRecord;
                if (currentPlacedSymbol && record.kind === 'display-property') {
                    attachDisplayPropertyToPlacedSymbol(currentPlacedSymbol, record);
                }
                if (currentDrawnInst && record.kind === 'display-property') {
                    attachDisplayPropertyToDrawnInst(currentDrawnInst, record);
                }
                if (currentPlacedSymbol && record.kind === 'port-instance') {
                    attachPortInstanceToPlacedSymbol(currentPlacedSymbol, record);
                }
                if (currentDrawnInst && record.kind === 'drawn-port') {
                    attachDrawnPortToDrawnInst(currentDrawnInst, record);
                }
                if (currentPlacedSymbol && record.kind === 'pin-to-wire') {
                    attachPinToWireToPlacedSymbol(currentPlacedSymbol, record);
                }
                counts[record.kind]++;
                if (records.length < maxRecords) {
                    records.push(record);
                    if (shouldIncludeRawRecords(options)) {
                        decodedRecords.push({
                            ...decodedNode,
                            value: record,
                        });
                    }
                }
                else {
                    truncated = true;
                }
                if (shouldIncludeDiagnostics(options)) {
                    diagnostics.push(...decodedNode.diagnostics);
                }
                if (record.kind === 'placed-symbol') {
                    const frame = typedOwners.get(record.offset);
                    if (frame) {
                        record.recordFrame = { offset: frame.offset, endOffset: frame.endOffset };
                        const countOffset = placementDisplayCounts.get(record.offset);
                        if (record.role === 'part-inst' && countOffset !== undefined) {
                            let cursor = countOffset + 2;
                            let valid = cursor <= frame.endOffset;
                            const count = valid ? OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, countOffset) : 0;
                            for (let index = 0; valid && index < count; index++) {
                                const child = displayFrames.get(cursor);
                                if (!child || child.endOffset > frame.endOffset) {
                                    valid = false;
                                }
                                else {
                                    cursor = child.endOffset;
                                }
                            }
                            const length = cursor + 3 <= frame.endOffset ? OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, cursor + 1) : 0;
                            const end = cursor + 3 + length;
                            const valueStringIndex = end + 5 <= frame.endOffset ? OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, end + 1) : undefined;
                            if (valid &&
                                bytes[cursor] === 0x18 &&
                                bytes[end] === 0 &&
                                !bytes.subarray(cursor + 3, end).some((byte) => byte < 0x20 || byte > 0x7e) &&
                                valueStringIndex !== undefined) {
                                record.referenceTail = {
                                    offset: cursor + 1,
                                    reference: OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.bytesToAscii(bytes, cursor + 3, end),
                                    valueStringIndex,
                                };
                                if (valueStringIndex > 0 && options.libraryStringCatalog?.[valueStringIndex] === undefined) {
                                    record.referenceState = OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.PlacementReferenceState.Withheld;
                                    record.valueState = OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.PlacementValueState.Withheld;
                                    diagnostics.push({
                                        severity: 'warning',
                                        code: 'placement-value-withheld',
                                        offset: end + 1,
                                        streamPath: options.streamPath ?? '',
                                        message: `CFB placement value: catalogue index ${valueStringIndex} does not resolve; value withheld`,
                                    });
                                    diagnostics.push({
                                        severity: 'warning',
                                        code: 'placement-reference-tail-withheld',
                                        offset: cursor,
                                        streamPath: options.streamPath ?? '',
                                        message: `CFB placement reference: tail catalogue index ${valueStringIndex} does not resolve; reference and connectivity withheld`,
                                    });
                                }
                            }
                            else {
                                record.referenceState = OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.PlacementReferenceState.Withheld;
                                diagnostics.push({
                                    severity: 'warning',
                                    code: 'placement-reference-tail-withheld',
                                    offset: cursor,
                                    streamPath: options.streamPath ?? '',
                                    message: 'CFB placement reference: incomplete counted display list or non-printable or incomplete reference tail',
                                });
                            }
                        }
                    }
                    if (pendingPropertyPairsPayload) {
                        const decodedPairs = OrcadDsnCfbDeclarativeSpecs_1.OrcadDsnCfbDeclarativeSpecs.decodeTailPropertyIdPairs(pendingPropertyPairsPayload);
                        if (record.role === 'global') {
                            decodedPairs.pairs = (0, OrcadDsnCfbPagePropertyFrames_1.readPagePropertyFrame)(pendingPropertyPairsPayload, 0x25, reportPropertyFrame);
                            if (decodedPairs.unrecognizedTag === 0x25) {
                                if (decodedPairs.pairs === undefined) {
                                    reportPropertyFrame('global-property-unframed');
                                }
                                decodedPairs.unrecognizedTag = undefined;
                            }
                        }
                        if (decodedPairs.pairs) {
                            record.propertyPairs = decodedPairs.pairs;
                        }
                        if (decodedPairs.unrecognizedTag !== undefined) {
                            diagnostics.push({
                                severity: 'warning',
                                code: 'property-pair-block-unknown-tag',
                                message: `Property row pair block carries tag 0x${decodedPairs.unrecognizedTag.toString(16)}, whose owning object kind is unknown, so its rows were not read`,
                                specName: 'page.propertyPairs',
                                streamPath: options.streamPath ?? '',
                                details: { tag: decodedPairs.unrecognizedTag },
                            });
                        }
                        pendingPropertyPairsPayload = undefined;
                    }
                    currentPlacedSymbol = record;
                    currentDrawnInst = undefined;
                    currentDrawnFrame = undefined;
                }
                if (record.kind === 'wire' && pendingPropertyPairsPayload) {
                    record.propertyPairs = (0, OrcadDsnCfbPagePropertyFrames_1.readPagePropertyFrame)(pendingPropertyPairsPayload, 0x14, reportPropertyFrame);
                }
                if (record.kind === 'alias' && record.offset === payload.offset && aliasOwner !== undefined) {
                    record.ownerWireDbId = aliasOwner;
                }
                if (record.kind === 'wire' &&
                    record.offset === payload.offset &&
                    payload.payloadByteLength === 52 &&
                    payload.payload[31] === 0x31 &&
                    payload.payload[40] === 0x31 &&
                    payload.payload[49] === 0x31 &&
                    OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(payload.payload, 41) === 11 &&
                    OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(payload.payload, 50) === 0xffff) {
                    const count = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(payload.payload, 29);
                    if (count === 1) {
                        pendingAliasOwner = record.dbId;
                    }
                    else {
                        diagnostics.push({
                            severity: 'warning',
                            code: 'wire-alias-child-count-unsupported',
                            streamPath: options.streamPath ?? '',
                            offset: record.offset,
                            message: `CFB alias [wire-alias-child-count-unsupported] wire=${record.dbId} count=${count}`,
                        });
                    }
                }
                if (record.kind === 'drawn-inst') {
                    currentDrawnInst = record;
                    const frames = drawnOwners.filter((frame) => frame.dataOffset <= record.offset && record.offset < frame.endOffset);
                    currentDrawnFrame = frames.length === 1 ? frames[0] : undefined;
                    if (currentDrawnFrame) {
                        record.recordFrame = { offset: currentDrawnFrame.offset, endOffset: currentDrawnFrame.endOffset };
                    }
                    currentPlacedSymbol = undefined;
                }
                if ((record.kind === 'pin-to-wire' || record.kind === 'port-instance') && record.ownerName) {
                    closesCurrentPlacedSymbol = true;
                }
                if (record.kind === 'drawn-port' && record.ownerName) {
                    record.ownerReference = record.ownerName;
                    if (currentDrawnInst) {
                        currentDrawnInst.deviceDesignator = record.ownerName;
                        currentDrawnInst.reference = record.ownerName;
                    }
                    closesCurrentDrawnInst = true;
                }
            }
            if (closesCurrentPlacedSymbol) {
                currentPlacedSymbol = undefined;
            }
            if (closesCurrentDrawnInst) {
                currentDrawnInst = undefined;
            }
            pendingPropertyPairsPayload = payload.payload;
        });
        if (pendingAliasOwner !== undefined) {
            diagnostics.push({
                severity: 'warning',
                code: 'wire-alias-child-unresolved',
                streamPath: options.streamPath ?? '',
                offset: bytes.length,
                message: `CFB alias [wire-alias-child-unresolved] wire=${pendingAliasOwner}: child payload is absent`,
            });
        }
        return {
            records,
            counts,
            truncated,
            decodedByteCount,
            ...(shouldIncludeRawRecords(options) ? { decodedRecords } : {}),
            diagnostics: shouldIncludeDiagnostics(options) ? diagnostics : (diagnostics.filter((diagnostic) => diagnostic.code.startsWith('typed-frame-') ||
                diagnostic.code.startsWith('wire-alias-child-') ||
                diagnostic.code === 'page-property-frame-withheld' ||
                diagnostic.code === 'placement-reference-tail-withheld' ||
                diagnostic.code === 'placement-value-withheld' ||
                diagnostic.code === 'page-pin-frame-withheld')),
        };
    }
    OrcadDsnCfbRecordDecoders.decodeOrCadDsnCfbPageObjects = decodeOrCadDsnCfbPageObjects;
    function decodeSymbolRecordSpecNode(spec, record, options = {}) {
        return DsnCfbBinarySpec_1.DsnCfbBinarySpec.decodeRecordSpec(spec, {
            bytes: record.payload,
            offset: 0,
            streamPath: options.streamPath ?? '',
            streamName: options.streamName,
            payloadOffset: record.offset,
            payloadByteLength: record.payloadByteLength,
            byteLength: record.byteLength,
        });
    }
    function tailPinNumberMapRecordNode(bytes, offset, byteLength, options = {}, scanWork) {
        const decoded = DsnCfbBinarySpec_1.DsnCfbBinarySpec.decodeRecordSpec(OrcadDsnCfbDeclarativeSpecs_1.OrcadDsnCfbDeclarativeSpecs.ORCAD_DSN_CFB_SYMBOL_TAIL_RECORD_SPECS[0], {
            bytes,
            offset,
            streamPath: options.streamPath ?? '',
            streamName: options.streamName,
            payloadOffset: offset,
            payloadByteLength: byteLength,
            byteLength,
            extra: { symbolScanWork: scanWork },
        });
        if (!decoded) {
            return undefined;
        }
        const spanEnd = decoded.value.offset + decoded.value.byteLength;
        const unknownRanges = (decoded.unknownRanges ?? []).flatMap((range) => {
            const start = Math.max(range.offset, decoded.value.offset);
            const end = Math.min(range.offset + range.byteLength, spanEnd);
            return end > start ? [{ ...range, offset: start, byteLength: end - start }] : [];
        });
        const { unknownRanges: _budgetWideRanges, ...node } = decoded;
        return {
            ...node,
            span: {
                ...decoded.span,
                offset: decoded.value.offset,
                byteLength: decoded.value.byteLength,
                payloadOffset: decoded.value.offset,
                payloadByteLength: decoded.value.byteLength,
            },
            ...(unknownRanges.length > 0 ? { unknownRanges } : {}),
        };
    }
    function isPlausiblePadMap(record) {
        if (record.kind !== 'pin-number-map') {
            return false;
        }
        return (record.entries.length > 0 &&
            record.entries.every((entry, position) => entry.position === position) &&
            !record.entries.some((entry) => SOURCE_LIBRARY_PATH_PATTERN.test(entry.number)));
    }
    function decodeSymbolObjectRecordNodes(record, options = {}, pinFrame, diagnostics) {
        if (decodePrimitiveRecordNode(record.payload, 0)) {
            return [];
        }
        const inlinePinNumberMap = decodeSymbolRecordSpecNode(OrcadDsnCfbDeclarativeSpecs_1.OrcadDsnCfbDeclarativeSpecs.ORCAD_DSN_CFB_SYMBOL_RECORD_SPECS[0], record, options);
        if (inlinePinNumberMap) {
            return [inlinePinNumberMap];
        }
        const viewHeader = decodeSymbolRecordSpecNode(OrcadDsnCfbDeclarativeSpecs_1.OrcadDsnCfbDeclarativeSpecs.ORCAD_DSN_CFB_SYMBOL_RECORD_SPECS[1], record, options);
        const symbolPin = decodeSymbolRecordSpecNode(OrcadDsnCfbDeclarativeSpecs_1.OrcadDsnCfbDeclarativeSpecs.ORCAD_DSN_CFB_SYMBOL_RECORD_SPECS[2], record, options);
        const pin = symbolPin?.value.kind === 'symbol-pin' ? symbolPin.value : undefined;
        const pinName = pin === undefined ? undefined : OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readLengthPrefixedAsciiStringAt(record.payload, 0);
        const pinBodyOffset = pinName === undefined ? undefined : OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.lengthPrefixedStringEnd(pinName);
        const hasPinFrame = pinFrame !== undefined &&
            pinBodyOffset !== undefined &&
            pinFrame.dataOffset === record.offset &&
            record.offset + pinBodyOffset + 24 <= pinFrame.endOffset;
        if (symbolPin && !hasPinFrame) {
            diagnostics?.push({
                severity: 'warning',
                code: 'cache-pin-frame-withheld',
                offset: record.offset,
                streamPath: options.streamPath ?? '',
                message: `CFB symbol pin [cache-pin-frame-withheld] offset=${record.offset}: no complete typed pin frame; pin withheld`,
            });
        }
        if (viewHeader && !hasPinFrame) {
            return [viewHeader];
        }
        if (symbolPin && hasPinFrame) {
            return [symbolPin];
        }
        if (viewHeader) {
            return [viewHeader];
        }
        const records = [];
        const displayProperty = decodeSymbolRecordSpecNode(OrcadDsnCfbDeclarativeSpecs_1.OrcadDsnCfbDeclarativeSpecs.ORCAD_DSN_CFB_SYMBOL_RECORD_SPECS[3], record, options);
        if (displayProperty) {
            records.push(displayProperty);
        }
        const partValue = decodeSymbolRecordSpecNode(OrcadDsnCfbDeclarativeSpecs_1.OrcadDsnCfbDeclarativeSpecs.ORCAD_DSN_CFB_SYMBOL_RECORD_SPECS[4], record, options);
        if (partValue) {
            records.push(partValue);
        }
        if (records.length > 0) {
            return records;
        }
        const namedRecord = decodeSymbolRecordSpecNode(OrcadDsnCfbDeclarativeSpecs_1.OrcadDsnCfbDeclarativeSpecs.ORCAD_DSN_CFB_SYMBOL_RECORD_SPECS[5], record, options);
        return namedRecord ? [namedRecord] : [];
    }
    function decodeOrCadDsnCfbSymbolObjects(bytes, options = {}) {
        const diagnostics = [];
        const typedPins = (0, OrcadDsnCfbTypedObjectFrames_1.readTypedObjectFrames)(bytes, [0x1a, 0x1b], 1, diagnostics, options.streamPath);
        const maxRecords = options.maxRecords ?? Number.MAX_SAFE_INTEGER;
        const records = [];
        const decodedRecords = [];
        const counts = createSymbolObjectCounts();
        const scanWork = { candidateOffsets: 0, mapStringProbes: 0 };
        let truncated = false;
        let decodedByteCount = 0;
        let tailOffset = bytes.length;
        const isCacheRoleStream = DsnCfbBinarySpec_1.DsnCfbBinarySpec.matchStreamSpecs(OrcadDsnCfbStreamSpecs_1.ORCAD_DSN_CFB_STREAM_SPECS, {
            path: options.streamPath ?? '',
            name: options.streamName ?? '',
            type: CFB_STREAM_ENTRY_TYPE,
        }).some((spec) => spec.name === CACHE_STREAM_SPEC_NAME);
        const consumedBytes = isCacheRoleStream ? new Uint8Array(bytes.length) : undefined;
        const markConsumed = (start, byteLength) => {
            if (!consumedBytes) {
                return;
            }
            const end = Math.min(bytes.length, start + byteLength);
            for (let i = Math.max(0, start); i < end; i++) {
                consumedBytes[i] = 1;
            }
        };
        const hasConsumedByte = (start, byteLength) => {
            if (!consumedBytes) {
                return false;
            }
            const end = Math.min(bytes.length, start + byteLength);
            for (let i = Math.max(0, start); i < end; i++) {
                if (consumedBytes[i]) {
                    return true;
                }
            }
            return false;
        };
        const pendingNamedRecords = [];
        const addDecoded = (decodedNode) => {
            if (isCacheRoleStream && decodedNode.value.kind === 'named-record') {
                pendingNamedRecords.push(decodedNode);
                return;
            }
            const decoded = decodedNode.value;
            counts[decoded.kind]++;
            decodedByteCount += decoded.byteLength;
            markConsumed(decoded.offset, decoded.byteLength);
            if (records.length < maxRecords) {
                records.push(decoded);
                if (shouldIncludeRawRecords(options)) {
                    decodedRecords.push(decodedNode);
                }
            }
            else {
                truncated = true;
            }
            if (shouldIncludeDiagnostics(options)) {
                diagnostics.push(...decodedNode.diagnostics);
            }
        };
        OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.forEachTerminatedPayload(bytes, (payload) => {
            if (payload.byteLength === payload.payloadByteLength) {
                const tail = tailPinNumberMapRecordNode(bytes, payload.offset, payload.byteLength, options);
                if (tail && isPlausiblePadMap(tail.value)) {
                    tailOffset = payload.offset + payload.byteLength;
                    addDecoded(tail);
                    markConsumed(payload.offset, payload.byteLength);
                    return;
                }
                tailOffset = payload.offset;
            }
            else {
                tailOffset = payload.offset + payload.byteLength;
            }
            const decodedNodes = decodeSymbolObjectRecordNodes(payload, options, typedPins.get(payload.offset), diagnostics);
            if (decodedNodes.length === 0) {
                return;
            }
            tailOffset = payload.offset + payload.byteLength;
            for (const decoded of decodedNodes) {
                addDecoded(decoded);
            }
        });
        if (isCacheRoleStream) {
            let cursor = 0;
            while (cursor < bytes.length) {
                scanWork.candidateOffsets++;
                if (hasConsumedByte(cursor, 1)) {
                    cursor++;
                    continue;
                }
                if (!OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readLengthPrefixedAsciiStringAt(bytes, cursor, { maxLength: 256 })) {
                    cursor++;
                    continue;
                }
                const found = tailPinNumberMapRecordNode(bytes, cursor, bytes.length - cursor, options, scanWork);
                if (found && isPlausiblePadMap(found.value) && !hasConsumedByte(found.value.offset, found.value.byteLength)) {
                    addDecoded(found);
                    cursor = Math.max(cursor + 1, found.value.offset + found.value.byteLength);
                    continue;
                }
                cursor++;
            }
            for (const pending of pendingNamedRecords) {
                const decoded = pending.value;
                if (hasConsumedByte(decoded.offset, decoded.byteLength)) {
                    continue;
                }
                counts[decoded.kind]++;
                decodedByteCount += decoded.byteLength;
                markConsumed(decoded.offset, decoded.byteLength);
                if (records.length < maxRecords) {
                    records.push(decoded);
                    if (shouldIncludeRawRecords(options)) {
                        decodedRecords.push(pending);
                    }
                }
                else {
                    truncated = true;
                }
                if (shouldIncludeDiagnostics(options)) {
                    diagnostics.push(...pending.diagnostics);
                }
            }
        }
        else {
            const plausibleTailAt = (offset, searchByteLength) => {
                const candidate = tailPinNumberMapRecordNode(bytes, offset, searchByteLength, options);
                return candidate && isPlausiblePadMap(candidate.value) ? candidate : undefined;
            };
            const tailByteLength = bytes.length - tailOffset;
            let tail = tailByteLength > 0 ? plausibleTailAt(tailOffset, tailByteLength) : undefined;
            for (let offset = Math.max(tailOffset, bytes.length - 512); tailByteLength > 0 && !tail && offset < bytes.length; offset++) {
                tail = plausibleTailAt(offset, bytes.length - offset);
            }
            if (tail) {
                addDecoded(tail);
            }
        }
        return {
            records,
            counts,
            truncated,
            decodedByteCount,
            scanWork,
            ...(shouldIncludeRawRecords(options) ? { decodedRecords } : {}),
            diagnostics: shouldIncludeDiagnostics(options) ? diagnostics : (diagnostics.filter((diagnostic) => diagnostic.code === 'cache-pin-frame-withheld' || diagnostic.code.startsWith('typed-frame-'))),
        };
    }
    OrcadDsnCfbRecordDecoders.decodeOrCadDsnCfbSymbolObjects = decodeOrCadDsnCfbSymbolObjects;
})(OrcadDsnCfbRecordDecoders || (exports.OrcadDsnCfbRecordDecoders = OrcadDsnCfbRecordDecoders = {}));
//# sourceMappingURL=OrcadDsnCfbRecordDecoders.js.map